import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  scraperSourcesApi,
  scraperTriggersApi,
  scraperScheduleApi,
  scraperStatsApi,
  scraperCaseStatsApi,
  type ScraperCaseStats,
} from '../services/scraperApi';
import API from '../services/api';
import type {
  ScraperSource,
  ScraperSourceType,
  ScraperSourceCategory,
  ScraperTrigger,
  ScraperTierSchedule,
  ScraperStats,
} from '../types/scraper';
import { formatDistanceToNow } from 'date-fns';
import { formatIsoDate } from '../utils/formatDateValue';
import { useToast } from '../contexts/ToastContext';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import DuplicatesTab from '../components/DuplicatesTab';

type TopLevel = 'validation' | 'duplicates' | 'config';
type ConfigTab = 'sources' | 'runs';
// URL names for the sections (?section=review|duplicates|scraper), so the dashboard and
// bookmarks can land on the right one. Sub-tabs travel as ?tab=.
const SECTION_TO_URL: Record<TopLevel, string> = { validation: 'review', duplicates: 'duplicates', config: 'scraper' };
const URL_TO_SECTION: Record<string, TopLevel> = { review: 'validation', duplicates: 'duplicates', scraper: 'config' };

const typeLabels: Record<ScraperSourceType, string> = {
  FACEBOOK: 'Facebook',
  TWITTER: 'Twitter / X',
  HTML: 'HTML',
  RSS: 'RSS Feed',
};

const categoryLabels: Record<ScraperSourceCategory, string> = {
  GOVERNMENT: 'Government',
  NGO: 'NGO',
  NEWS_OUTLET: 'News Outlet',
  COMMUNITY: 'Community',
  INTERNATIONAL_ORG: 'International Org',
  INDIGENOUS_ORG: 'Indigenous Org',
  HUMAN_RIGHTS_ORG: 'Human Rights Org',
  INDIGENOUS_MEDIA: 'Indigenous Media',
  NETWORK_COALITION: 'Network / Coalition',
};

// Colour rule: neutral pills everywhere; red only for failure, amber only for a warning.
const NEUTRAL_PILL = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

// Tier names as the scraper itself labels them; how often each runs is whatever the scraper's
// deployment says, shown on the Runs tab.
const tierLabels: Record<number, { label: string; color: string }> = {
  1: { label: 'Tier 1 · emergency', color: NEUTRAL_PILL },
  2: { label: 'Tier 2 · government', color: NEUTRAL_PILL },
  3: { label: 'Tier 3 · community', color: NEUTRAL_PILL },
  4: { label: 'Tier 4 · twice a week', color: NEUTRAL_PILL },
  5: { label: 'Tier 5 · weekly', color: NEUTRAL_PILL },
};

// Plain words for the common cron shapes; anything else is shown raw.
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [m, h, dom, mon, dow] = parts;
  if (!/^\d+$/.test(m) || dom !== '*' || mon !== '*') return expr;
  const every = /^\*\/(\d+)$/.exec(h);
  if (every && dow === '*') return `Every ${every[1]} hours`;
  if (!/^\d+$/.test(h)) return expr;
  const time = `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  if (dow === '*') return `Daily at ${time}`;
  if (/^[0-6](,[0-6])*$/.test(dow)) return `${dow.split(',').map((d) => DOW[Number(d)]).join(', ')} at ${time}`;
  return expr;
}

// Loading / error states are rendered per section so one failing request doesn't blank the page.
function SectionSkeleton({ rows = 4, inCard = false }: { rows?: number; inCard?: boolean }) {
  return (
    <div className={`animate-pulse space-y-3 ${inCard ? 'p-6' : ''}`}>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className={`rounded-lg ${inCard ? 'bg-gray-100 dark:bg-gray-700 h-10' : 'bg-white dark:bg-gray-800 shadow h-16'}`} />
      ))}
    </div>
  );
}

function SectionError({ what, error, onRetry }: { what: string; error: unknown; onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm">
      <p className="font-medium text-red-700 dark:text-red-300">Couldn't load {what}</p>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{error instanceof Error ? error.message : 'Unknown error'}</p>
      <button type="button" onClick={onRetry} className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300 underline hover:no-underline">
        Try again
      </button>
    </div>
  );
}

const statusIcons: Record<string, React.ReactNode> = {
  OK: <CheckCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />,
  ERROR: <XCircleIcon className="h-4 w-4 text-red-500" />,
  BLOCKED: <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />,
};

interface FormState {
  open: boolean;
  editing: ScraperSource | null;
  type: ScraperSourceType;
  name: string;
  url: string;
  category: ScraperSourceCategory;
  tier: 1 | 2 | 3 | 4 | 5;
  maxItems: number;
  keywords: string;
  country: string;
  enabled: boolean;
}

const emptyForm: FormState = {
  open: false,
  editing: null,
  type: 'HTML',
  name: '',
  url: '',
  category: 'NEWS_OUTLET',
  tier: 2,
  maxItems: 5,
  keywords: '',
  country: '',
  enabled: true,
};

export default function Scraper() {
  const { user } = useAuth();
  // Sources, schedules and runs are Admin-only on the backend (/api/admin/scraper/*); don't
  // show them to anyone else.
  const isAdmin = user?.role === 'ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const [topLevel, setTopLevel] = useState<TopLevel>(() => {
    const s = URL_TO_SECTION[searchParams.get('section') || ''] || 'validation';
    return s === 'config' && !isAdmin ? 'validation' : s;
  });
  const [configTab, setConfigTab] = useState<ConfigTab>(() => (searchParams.get('section') === 'scraper' && searchParams.get('tab') === 'runs' ? 'runs' : 'sources'));
  const [subTab, setSubTab] = useState<string | null>(() => (searchParams.get('section') === 'duplicates' ? searchParams.get('tab') : null));
  // Source-table filters live in the URL too (?type=&category=&region=&enabled=) so a filtered
  // view can be bookmarked or shared.
  const [sourceSearch, setSourceSearch] = useState<string>(() => searchParams.get('q') || '');
  const [typeFilter, setTypeFilter] = useState<string>(() => searchParams.get('type') || '');
  const [categoryFilter, setCategoryFilter] = useState<string>(() => searchParams.get('category') || '');
  const [enabledFilter, setEnabledFilter] = useState<string>(() => searchParams.get('enabled') || '');
  const [regionFilter, setRegionFilter] = useState<string>(() => searchParams.get('region') || '');
  // URL → state, so in-page links (the overview card) and the back button switch sections.
  // Only writes state that differs, so it converges with the mirror effect below instead of looping.
  useEffect(() => {
    const s = URL_TO_SECTION[searchParams.get('section') || ''] || 'validation';
    const nextTop = s === 'config' && !isAdmin ? 'validation' : s;
    const tab = searchParams.get('tab');
    if (nextTop !== topLevel) setTopLevel(nextTop);
    if (nextTop === 'config') {
      const nextConfig: ConfigTab = tab === 'runs' ? 'runs' : 'sources';
      if (nextConfig !== configTab) setConfigTab(nextConfig);
    } else if (nextTop === 'duplicates' && (tab || null) !== subTab) {
      setSubTab(tab || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // Mirror section + sub-tab into the URL (replace, so the back button isn't flooded).
  useEffect(() => {
    const next = new URLSearchParams();
    if (topLevel !== 'validation') next.set('section', SECTION_TO_URL[topLevel]);
    const tab = topLevel === 'config' ? (configTab !== 'sources' ? configTab : null) : topLevel === 'duplicates' ? subTab : null;
    if (tab) next.set('tab', tab);
    if (topLevel === 'config' && configTab === 'sources') {
      if (sourceSearch.trim()) next.set('q', sourceSearch.trim());
      if (typeFilter) next.set('type', typeFilter);
      if (categoryFilter) next.set('category', categoryFilter);
      if (regionFilter) next.set('region', regionFilter);
      if (enabledFilter) next.set('enabled', enabledFilter);
    }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevel, configTab, subTab, sourceSearch, typeFilter, categoryFilter, regionFilter, enabledFilter]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [triggerTier, setTriggerTier] = useState<string>('');
  const [triggerSourceType, setTriggerSourceType] = useState<string>('');

  // Stats
  const { data: stats } = useQuery<ScraperStats>({
    queryKey: ['scraper-stats'],
    queryFn: () => scraperStatsApi.get(),
    enabled: isAdmin,
  });

  // Case status counts (same source as the Needs Review sub-tab, so the KPI can't disagree with it).
  const { data: caseStats } = useQuery<ScraperCaseStats>({
    queryKey: ['scraper-case-stats'],
    queryFn: () => scraperCaseStatsApi.get(),
  });
  // Open duplicate matches awaiting a confirm/dismiss decision (the Needs Review dedup queue).
  const { data: pendingMatches } = useQuery<Array<{ id: string }>>({
    queryKey: ['dedup-pending-matches'],
    queryFn: async () => (await API.get('/api/dedup/pending-matches')).data,
  });

  // Sources tab queries
  const {
    data: scraperSources,
    isLoading: sourcesLoading,
    error: sourcesError,
    refetch: refetchSources,
  } = useQuery<ScraperSource[]>({
    queryKey: ['scraper-sources', typeFilter, categoryFilter, enabledFilter, regionFilter],
    queryFn: () => {
      const filters: { type?: ScraperSourceType; category?: ScraperSourceCategory; enabled?: boolean; region?: string } = {};
      if (typeFilter) filters.type = typeFilter as ScraperSourceType;
      if (categoryFilter) filters.category = categoryFilter as ScraperSourceCategory;
      if (enabledFilter) filters.enabled = enabledFilter === 'true';
      if (regionFilter) filters.region = regionFilter;
      return scraperSourcesApi.getAll(Object.keys(filters).length > 0 ? filters : undefined);
    },
    enabled: topLevel === 'config' && configTab === 'sources',
  });

  // Runs tab queries
  const {
    data: triggers,
    isLoading: triggersLoading,
    error: triggersError,
    refetch: refetchTriggers,
  } = useQuery<ScraperTrigger[]>({
    queryKey: ['scraper-triggers'],
    queryFn: () => scraperTriggersApi.getRecent(),
    refetchInterval: 15000,
    enabled: topLevel === 'config' && configTab === 'runs',
  });

  const {
    data: tierSchedule,
    isLoading: tierScheduleLoading,
    error: tierScheduleError,
    refetch: refetchTierSchedule,
  } = useQuery<ScraperTierSchedule[]>({
    queryKey: ['scraper-tier-schedule'],
    queryFn: () => scraperScheduleApi.get(),
    refetchInterval: 60000,
    enabled: topLevel === 'config' && configTab === 'runs',
  });

  // Trigger mutation
  const triggerMutation = useMutation({
    mutationFn: (data: { tier?: number; sourceType?: ScraperSourceType }) =>
      scraperTriggersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-triggers'] });
      queryClient.invalidateQueries({ queryKey: ['scraper-stats'] });
      showToast('success', 'Scrape run queued');
    },
    onError: () => showToast('error', 'Failed to trigger scraper'),
  });

  // Source mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<ScraperSource>) => scraperSourcesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setForm(emptyForm);
      showToast('success', 'Source created');
    },
    onError: () => showToast('error', 'Failed to create source'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ScraperSource> }) =>
      scraperSourcesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setForm(emptyForm);
      showToast('success', 'Source updated');
    },
    onError: () => showToast('error', 'Failed to update source'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => scraperSourcesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setDeleteConfirm(null);
      showToast('success', 'Source deleted');
    },
    onError: () => showToast('error', 'Failed to delete source'),
  });

  const toggleEnabled = (source: ScraperSource) => {
    updateMutation.mutate({ id: source.id, data: { enabled: !source.enabled } });
  };

  const openCreate = () => {
    setForm({ ...emptyForm, open: true });
  };

  const openEdit = (source: ScraperSource) => {
    setForm({
      open: true,
      editing: source,
      type: source.type,
      name: source.name,
      url: source.url,
      category: source.category,
      tier: source.tier,
      maxItems: source.maxItems,
      keywords: source.keywords ? source.keywords.join(', ') : '',
      country: source.country || '',
      enabled: source.enabled,
    });
  };

  const handleSubmit = () => {
    const keywordsArray = form.keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const data: Partial<ScraperSource> = {
      type: form.type,
      name: form.name,
      url: form.url,
      category: form.category,
      tier: form.tier,
      maxItems: form.maxItems,
      keywords: keywordsArray.length > 0 ? keywordsArray : null,
      country: form.country || null,
      enabled: form.enabled,
    };

    if (form.editing) {
      updateMutation.mutate({ id: form.editing.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div>
      {/* Form Modal */}
      {form.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {form.editing ? 'Edit Source' : 'Add Source'}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as ScraperSourceType })}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    {Object.entries(typeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value as ScraperSourceCategory })
                    }
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Reuters Indigenous Rights"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL</label>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://example.com/news/indigenous"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use the page that lists the articles: a section, tag, search-results or RSS page where new stories appear.
                  A homepage or a single article gives the scraper nothing to follow.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="e.g., Philippines"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Keywords <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    placeholder="indigenous, land rights, FPIC"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tier</label>
                  <select
                    value={form.tier}
                    onChange={(e) =>
                      setForm({ ...form, tier: parseInt(e.target.value, 10) as 1 | 2 | 3 | 4 | 5 })
                    }
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    <option value={1}>Tier 1 (4hr)</option>
                    <option value={2}>Tier 2 (8hr)</option>
                    <option value={3}>Tier 3 (24hr)</option>
                    <option value={4}>Tier 4 (twice a week)</option>
                    <option value={5}>Tier 5 (once a week)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Items</label>
                  <input
                    type="number"
                    value={form.maxItems}
                    onChange={(e) =>
                      setForm({ ...form, maxItems: parseInt(e.target.value, 10) || 5 })
                    }
                    min={1}
                    max={50}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setForm(emptyForm)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !form.name.trim() ||
                  !form.url.trim() ||
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {form.editing ? 'Save Changes' : 'Add Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Source</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure? This will permanently remove this scraper source.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page header: title and section switcher on one line. Counts ride on the pills, so the
          page doesn't need a separate KPI row before the content. */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Data Collection</h1>
        <div className="flex gap-1 rounded-lg bg-gray-100 dark:bg-gray-700 p-1 sm:w-auto w-full">
          {([
            { key: 'validation' as TopLevel, label: 'Review queue', Icon: ShieldCheckIcon, count: caseStats?.byStatus?.FOR_VALIDATION, title: 'Auto-sourced cases waiting for review' },
            { key: 'duplicates' as TopLevel, label: 'Duplicates', Icon: DocumentDuplicateIcon, count: pendingMatches?.length, title: 'Possible duplicates awaiting a decision' },
            ...(isAdmin ? [{ key: 'config' as TopLevel, label: 'Scraper', Icon: Cog6ToothIcon, count: stats?.sources.enabled, title: 'Active sources' }] : []),
          ]).map(({ key, label, Icon, count, title }) => (
            <button
              key={key}
              onClick={() => setTopLevel(key)}
              title={title}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1.5 ${
                topLevel === key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
              {count != null && (
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                  topLevel === key ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Config Sub-tabs */}
      {topLevel === 'config' && (
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex gap-6">
            {([{ key: 'sources' as ConfigTab, label: 'Sources' }, { key: 'runs' as ConfigTab, label: 'Runs' }]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setConfigTab(tab.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}


      {/* Sources Tab */}
      {topLevel === 'config' && configTab === 'sources' && (
        <>
          {/* Toolbar */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              value={sourceSearch}
              onChange={(e) => setSourceSearch(e.target.value)}
              placeholder="Search name, URL or keyword"
              aria-label="Search sources"
              className="w-full sm:w-64 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All types</option>
              {Object.entries(typeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All categories</option>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All regions</option>
              <option value="LAC">LAC</option>
              <option value="ASIA">Asia</option>
              <option value="AFRICA">Africa</option>
            </select>

            <select
              value={enabledFilter}
              onChange={(e) => setEnabledFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>

            <div className="flex-1" />

            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <PlusIcon className="h-4 w-4" />
              Add Source
            </button>
          </div>

          {/* Sources Table */}
          {sourcesLoading ? (
            <SectionSkeleton />
          ) : sourcesError ? (
            <SectionError what="sources" error={sourcesError} onRetry={() => refetchSources()} />
          ) : (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Items / Max
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Last Scraped
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Enabled
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {(() => {
                  const q = sourceSearch.trim().toLowerCase();
                  const rows = (scraperSources || []).filter((src) => !q
                    || src.name.toLowerCase().includes(q)
                    || src.url.toLowerCase().includes(q)
                    || (src.keywords || []).some((k) => k.toLowerCase().includes(q)));
                  return rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                      No scraper sources configured
                    </td>
                  </tr>
                ) : (
                  rows.map((source) => (
                    <tr key={source.id} className={!source.enabled ? 'opacity-50' : ''}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{source.name}</div>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs hover:text-gray-900 dark:hover:text-gray-100 hover:underline"
                          title={`Open ${source.url} in a new tab`}
                        >
                          {source.url}
                        </a>
                        {source.keywords && source.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {source.keywords.map((kw) => (
                              <span key={kw} className="inline-flex px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {typeLabels[source.type] || source.type}
                        </span>
                        <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
                          {categoryLabels[source.category] || source.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            tierLabels[source.tier]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {tierLabels[source.tier]?.label || `Tier ${source.tier}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {source.lastItemCount != null ? (
                          <span>{source.lastItemCount} <span className="text-gray-400 dark:text-gray-500">/ {source.maxItems}</span></span>
                        ) : (
                          source.maxItems
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {source.country || <span className="text-gray-400 dark:text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {source.lastStatus ? (
                          <div className="flex items-center gap-1">
                            {statusIcons[source.lastStatus] || (
                              <span className="h-4 w-4 rounded-full bg-gray-300 inline-block" />
                            )}
                            <span className="text-xs text-gray-600 dark:text-gray-400">{source.lastStatus}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Never</span>
                        )}
                        {source.lastError && (
                          <div
                            className="text-xs text-red-500 truncate max-w-[150px]"
                            title={source.lastError}
                          >
                            {source.lastError}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {source.lastScrapedAt ? (
                          <span title={formatIsoDate(source.lastScrapedAt) || String(source.lastScrapedAt)}>
                            {formatDistanceToNow(new Date(source.lastScrapedAt), {
                              addSuffix: true,
                            })}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleEnabled(source)}
                          disabled={updateMutation.isPending}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            source.enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 shadow transition duration-200 ease-in-out ${
                              source.enabled ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(source)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-primary"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(source.id)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                  );
                })()}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {/* Runs Tab */}
      {topLevel === 'config' && configTab === 'runs' && (
        <>
          {/* Effective schedule, as the scraper last reported it. Read-only: the cron lives in the
              scraper's deployment (CRON_TIER* env vars), so there is nothing to edit here. */}
          <div className="mb-6 bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Scheduled runs</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Set in the scraper's deployment; the scraper reports it here when it starts and after each run.
                </p>
              </div>
              {tierSchedule && tierSchedule.length > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Times in {tierSchedule[0].timezone} · reported {formatDistanceToNow(new Date(tierSchedule[0].reportedAt), { addSuffix: true })}
                </p>
              )}
            </div>
            {tierScheduleLoading ? (
              <SectionSkeleton rows={2} inCard />
            ) : tierScheduleError ? (
              <div className="p-4"><SectionError what="the schedule" error={tierScheduleError} onRetry={() => refetchTierSchedule()} /></div>
            ) : !tierSchedule || tierSchedule.length === 0 ? (
              <div className="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">
                The scraper hasn't reported its schedule yet. It does so on start-up, so this fills in once a scraper build that reports it is deployed.
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {tierSchedule.map((row) => {
                  const last = (triggers || []).find((t) => t.triggeredBy === 'scheduler' && t.tier === row.tier);
                  return (
                    <li key={row.tier} className="px-5 py-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-x-4 gap-y-1 text-sm">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{row.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">{row.cron ? describeCron(row.cron) : 'Not scheduled'}{row.cron && <span className="ml-1 text-gray-400 dark:text-gray-500 font-mono">{row.cron}</span>}</span>
                      </div>
                      <div className="text-gray-700 dark:text-gray-300">
                        {row.nextRunAt ? (
                          <span title={formatIsoDate(row.nextRunAt) || row.nextRunAt}>Next {formatDistanceToNow(new Date(row.nextRunAt), { addSuffix: true })}</span>
                        ) : <span className="text-gray-400 dark:text-gray-500">—</span>}
                      </div>
                      <div className="text-gray-700 dark:text-gray-300">
                        {last ? (
                          <span title={formatIsoDate(last.startedAt || last.createdAt) || undefined}>
                            Last run {formatDistanceToNow(new Date(last.startedAt || last.createdAt), { addSuffix: true })}
                            <span className={`ml-1.5 ${last.status === 'FAILED' ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'}`}>· {last.status.toLowerCase()}</span>
                          </span>
                        ) : <span className="text-gray-400 dark:text-gray-500">No recorded run yet</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Run Scraper */}
          <div className="mb-6 bg-white dark:bg-gray-800 shadow sm:rounded-md p-4">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mr-2">Run Scraper</h3>
              <select
                value={triggerTier}
                onChange={(e) => setTriggerTier(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm"
              >
                <option value="">All tiers</option>
                {Object.entries(tierLabels).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select
                value={triggerSourceType}
                onChange={(e) => setTriggerSourceType(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm"
              >
                <option value="">All sources</option>
                {Object.entries(typeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const data: { tier?: number; sourceType?: ScraperSourceType } = {};
                  if (triggerTier) data.tier = parseInt(triggerTier, 10);
                  if (triggerSourceType) data.sourceType = triggerSourceType as ScraperSourceType;
                  triggerMutation.mutate(data);
                }}
                disabled={triggerMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <PlayIcon className="h-4 w-4" />
                {triggerMutation.isPending ? 'Triggering...' : 'Run Now'}
              </button>
            </div>
          </div>

          {/* Recent Runs Table */}
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Runs</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Auto-refreshes every 15 seconds</p>
            </div>

            {triggersLoading ? (
              <SectionSkeleton rows={3} inCard />
            ) : triggersError ? (
              <div className="p-4">
                <SectionError what="recent runs" error={triggersError} onRetry={() => refetchTriggers()} />
              </div>
            ) : !triggers || triggers.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                No scraper runs yet. Use the controls above to trigger a run.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Started by
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Started
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Result
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {triggers.map((t: ScraperTrigger) => {
                    const statusColors: Record<string, string> = {
                      FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
                    };
                    const duration =
                      t.startedAt && t.completedAt
                        ? `${Math.round(
                            (new Date(t.completedAt).getTime() -
                              new Date(t.startedAt).getTime()) /
                              1000
                          )}s`
                        : t.status === 'RUNNING' && t.startedAt
                        ? 'running...'
                        : '-';
                    // The scraper reports { created, failed, reports, articles, status } (older rows may
                    // carry casesCreated); say what was actually counted rather than a fixed pair.
                    const result = t.result as Record<string, number | string> | null;
                    const created = result ? Number(result.created ?? result.casesCreated ?? 0) : null;
                    const resultSummary = result
                      ? [
                          `${created} case${created === 1 ? '' : 's'} created`,
                          result.reports != null ? `${result.reports} extracted` : null,
                          result.articles != null ? `${result.articles} articles` : null,
                          result.status && result.status !== 'ok' ? String(result.status) : null,
                        ].filter(Boolean).join(' · ')
                      : t.error
                      ? t.error.substring(0, 80)
                      : t.status === 'PENDING'
                      ? 'Waiting for the scraper to pick it up'
                      : '-';
                    const startedAt = t.startedAt || t.createdAt;
                    return (
                      <tr key={t.id}>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              statusColors[t.status] || NEUTRAL_PILL
                            }`}
                          >
                            {t.status === 'RUNNING' && (
                              <ArrowPathIcon className="h-3 w-3 animate-spin" />
                            )}
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {t.tier ? tierLabels[t.tier]?.label || `Tier ${t.tier}` : 'All tiers'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {t.sourceType ? typeLabels[t.sourceType] || t.sourceType : 'all'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {t.triggeredBy === 'scheduler' ? 'Schedule' : 'Admin'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          <span title={formatIsoDate(startedAt) || String(startedAt)}>
                            {formatDistanceToNow(new Date(startedAt), { addSuffix: true })}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{duration}</td>
                        <td
                          className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 truncate max-w-[250px]"
                          title={resultSummary}
                        >
                          {resultSummary}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Validation */}
      {topLevel === 'validation' && (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Auto-sourced cases</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Cases the scraper submitted are reviewed in the Cases list, where you can search, filter, sort and act in bulk.
              Select For Validation cases there to re-run AI validation on them.
            </p>
          </div>

          {/* Headline: what the scraper has produced so far and lately */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 grid grid-cols-3 sm:grid-cols-5 gap-4">
            {([
              { label: 'Sourced so far', value: caseStats?.total },
              { label: 'Last 7 days', value: caseStats?.recent?.last7d },
              { label: 'Last 30 days', value: caseStats?.recent?.last30d },
              { label: 'Approved', value: caseStats?.byStatus?.APPROVED ?? 0, tone: 'text-emerald-700 dark:text-emerald-400' },
              { label: 'Rejected', value: caseStats?.byStatus?.REJECTED ?? 0, tone: 'text-red-700 dark:text-red-400' },
            ] as { label: string; value: number | undefined; tone?: string }[]).map(({ label, value, tone }) => (
              <div key={label}>
                <p className={`text-2xl font-bold tabular-nums leading-none ${tone || 'text-gray-900 dark:text-gray-100'}`}>{value ?? '…'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* What needs a human now: three queues, lit amber while something is waiting. */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Needs action</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {([
                { status: 'DRAFT', label: 'Held for duplicate check', hint: 'Flagged as a possible duplicate on arrival. Confirm or dismiss the match.', action: 'Decide', to: '/scraper?section=duplicates' },
                { status: 'FOR_VALIDATION', label: 'Needs review', hint: 'Waiting for a human to check the AI findings and the sources.', action: 'Review', to: '/cases?dataSource=auto&status=FOR_VALIDATION' },
                { status: 'FOR_APPROVAL', label: 'For approval', hint: 'Reviewed and waiting for an approver.', action: 'Approve', to: '/cases?dataSource=auto&status=FOR_APPROVAL' },
              ] as { status: string; label: string; hint: string; action: string; to: string }[]).map(({ status, label, hint, action, to }) => {
                const count = caseStats?.byStatus?.[status] ?? 0;
                const waiting = caseStats != null && count > 0;
                return (
                  <Link key={status} to={to}
                    className={`group flex flex-col rounded-lg border p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40 ${waiting
                      ? 'border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500'
                      : 'border-gray-200 dark:border-gray-700'}`}>
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</span>
                      <span className={`shrink-0 inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-sm font-bold tabular-nums ${waiting
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {caseStats ? count : '…'}
                      </span>
                    </span>
                    <span className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex-1">{hint}</span>
                    <span className={`mt-3 inline-flex items-center gap-1 text-xs font-medium ${waiting ? 'text-amber-800 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'}`}>
                      {waiting ? action : 'Nothing waiting'}
                      {waiting && <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Where the rest ended up: a quiet list with a share of the total. */}
          <div className="px-6 pt-4 pb-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Outcomes</h3>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {([
              { status: 'IN_RESEARCH', label: 'In research', hint: 'Sent back for more work', to: '/cases?dataSource=auto&status=IN_RESEARCH' },
              { status: 'APPROVED', label: 'Approved', hint: 'Published cases', to: '/cases?dataSource=auto&status=APPROVED' },
              { status: 'REJECTED', label: 'Rejected', hint: 'Rejected after review', to: '/cases?dataSource=auto&status=REJECTED' },
              { status: 'ARCHIVED', label: 'Archived as duplicate', hint: 'Confirmed duplicates, merged into the case they repeat', to: '/scraper?section=duplicates&tab=archived' },
              { status: null, label: 'All auto-sourced cases', hint: 'Every case the scraper submitted', to: '/cases?dataSource=auto' },
            ] as { status: string | null; label: string; hint: string; to: string }[]).map(({ status, label, hint, to }) => {
              const total = caseStats?.total ?? 0;
              const count = status ? caseStats?.byStatus?.[status] ?? 0 : total;
              const share = status && total > 0 ? Math.round((count / total) * 100) : null;
              return (
                <li key={label}>
                  <Link to={to} className="flex items-center justify-between gap-4 px-6 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <span className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 truncate">{hint}</span>
                    </span>
                    <span className="inline-flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                      {share != null && <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{share}%</span>}
                      <span className="tabular-nums min-w-[2ch] text-right">{caseStats ? count : '…'}</span>
                      <ArrowRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Duplicates */}
      {topLevel === 'duplicates' && <DuplicatesTab initialTab={subTab} onTabChange={setSubTab} />}
    </div>
  );
}
